import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { createForm, ISurveyBuilder } from './common/create-form.helpers';
import { of, Subject } from 'rxjs';
import { catchError, map, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UnsubscribeService } from '@core/services/unsubscribe.service';
import { SurveyService } from '@core/services/survey.service';
import { ToastrService } from '@core/services/toastr.service';

export enum EAnswerType {
  Single = 1,
  Multiple = 2
}

@Component({
  selector: 'gcc-csat-builder',
  templateUrl: './csat-builder.component.html',
  styleUrls: ['./csat-builder.component.scss'],
  standalone: false
})
export class CsatBuilderComponent implements OnInit {
  public isEditMode = false;
  public isChanged = false;
  public form: FormGroup;
  public types = EAnswerType;

  public load$ = new Subject<null>();
  public save$ = new Subject<ISurveyBuilder>();

  private previuseValue: string;

  get options(): FormArray {
    return this.form.get('options') as FormArray;
  }

  constructor(
    private surveyBuilderService: SurveyService,
    private toaster: ToastrService,
    private unsubscribe$: UnsubscribeService
  ) {}

  public ngOnInit(): void {
    this.handlerApis();
    this.loadData();
  }

  public cancel(): void {
    this.isEditMode = false;
    this.loadData();
  }

  public loadData(): void {
    this.load$.next(null);
  }

  public onSubmit(): void {
    this.save$.next(this.form.value as ISurveyBuilder);
  }

  public toggleEditMode() {
    this.isEditMode = !this.isEditMode;
    this.isChanged = false;
    this.options.controls.forEach(control => {
      if (this.isEditMode) {
        control.enable();
        this.form.controls.answerType.enable();
      } else {
        control.disable();
        this.form.controls.answerType.disable();
      }
    });
  }

  public addOption(optionText: string = '', idx = null): void {
    this.options.push(
      new FormGroup({
        text: new FormControl({ value: optionText, disabled: !this.isEditMode }, [
          Validators.required,
          Validators.maxLength(100)
        ]),
        id: new FormControl(null),
        order: new FormControl(this.options.length + 1)
      })
    );
  }

  public removeOption(index: number): void {
    if (!this.isEditMode) {
      return;
    }
    this.options.removeAt(index);
    this.options.controls.forEach((control: FormGroup, idx: number) => {
      control.controls.order.setValue(idx + 1);
    });
  }

  public isMultiSelect(): boolean {
    return this.form.get('answerType')?.value === EAnswerType.Multiple;
  }

  private handlerApis(): void {
    this.load$
      .pipe(
        tap(() => (this.form = null)),
        switchMap(() => this.surveyBuilderService.getBuilderData()),
        tap(surveyData => {
          this.form = createForm(surveyData);

          this.options.controls.forEach(element => element.enable());
          this.form.get('answerType').enable();

          this.previuseValue = this.form.value;

          this.form.get('answerType').disable();
          this.options.controls.forEach(element => element.disable());
        }),
        switchMap(() => this.form.valueChanges),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(value => {
        this.isChanged = JSON.stringify(this.previuseValue) !== JSON.stringify(value);
      });

    this.save$
      .pipe(
        switchMap(data => {
          const options = this.options.value;
          options.forEach((item, index) => {
            if (!item.text) {
              this.options.removeAt(index);
            }
          });

          this.options.controls = this.options.controls.filter((group: FormGroup) => {
            return !!group.controls.text.value;
          });
          this.options.controls.forEach((group: FormGroup) => {
            group.controls.text.markAsDirty();
            group.controls.text.markAsPending();
          });
          data.options = data.options.filter(item => !!item.text);
          return this.surveyBuilderService.saveBuilderData(data).pipe(
            map(() => true),
            catchError(() => of(null))
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(value => {
        if (!value) {
          return;
        }

        this.previuseValue = this.form.value;
        this.toggleEditMode();
        this.toaster.success('Data has been saved successfully!', 2000);
      });
  }
}
